import { ApiRoutes, AppPaginationOptions, QueryError, WithPagination } from '@/common/interfaces';
import { apiExecutor, buildMutation, buildQuery } from '@/common/services';

import { ProxyServer, ProxyServerFormFields, ProxyServersQueryKeys, SupportedLinuxOS } from '@/modules/ProxyServers';

export const useGetPasswordForProxyServersMutation = (proxyServerId: string) =>
  buildQuery<Pick<ProxyServer, 'password'>, QueryError>([ProxyServersQueryKeys.ShowPassword, proxyServerId], async () => {
    const { data } = await apiExecutor.get<Pick<ProxyServer, 'password'>>(
      ApiRoutes.ProxyServersByIdPassword.replace(':proxyServerId', proxyServerId),
    );
    return data;
  });

export const useGetAllProxyServersQuery = (paginationOptions: AppPaginationOptions, search?: string) =>
  buildQuery<WithPagination<ProxyServer>, QueryError>(
    [ProxyServersQueryKeys.GetAll, { ...paginationOptions, search }],
    async () => {
      const { data } = await apiExecutor.get<WithPagination<ProxyServer>>(ApiRoutes.ProxyServers, {
        params: { ...paginationOptions, search },
      });
      return data;
    },
  );

export const useGetOneByIdProxyServersQuery = (id: string) =>
  buildQuery<ProxyServer, QueryError>([ProxyServersQueryKeys.GetOneById, id], async () => {
    const { data } = await apiExecutor.get<ProxyServer>(ApiRoutes.ProxyServersById.replace(':proxyServerId', id));
    return data;
  });

export const useCreateProxyServersMutation = () =>
  buildMutation<ProxyServer, QueryError, ProxyServerFormFields>([ProxyServersQueryKeys.Create], async (postData) => {
    const { data } = await apiExecutor.post<ProxyServer>(ApiRoutes.ProxyServers, {
      ...postData,
    });
    return data;
  });

export const useUpdateProxyServersMutation = (id: string) =>
  buildMutation<ProxyServer, QueryError, Partial<ProxyServerFormFields>>([ProxyServersQueryKeys.Edit, id], async (postData) => {
    const { data } = await apiExecutor.patch<ProxyServer>(ApiRoutes.ProxyServersById.replace(':proxyServerId', id), {
      ...postData,
    });
    return data;
  });

export const useDeleteProxyServersMutation = (id: string) =>
  buildMutation<void, QueryError, void>([ProxyServersQueryKeys.Delete, id], async () => {
    await apiExecutor.delete<void>(ApiRoutes.ProxyServersById.replace(':proxyServerId', id));
  });

export const useAutocompleteProxyServersQuery = (params: { query: string; includedIds?: string[] }) =>
  buildQuery<Pick<ProxyServer, 'id' | 'name'>[], QueryError>([ProxyServersQueryKeys.Autocomplete, params], async () => {
    const { data } = await apiExecutor.get<Pick<ProxyServer, 'id' | 'name'>[]>(ApiRoutes.ProxyServersAutocomplete, {
      params: { q: params.query, includedIds: params?.includedIds?.length ? String(params.includedIds) : undefined },
    });
    return data;
  });

export const useBreadcrumbProxyServersQuery = (id: string) =>
  buildQuery<string, QueryError>([ProxyServersQueryKeys.Breadcrumb, id], async () => {
    const { data } = await apiExecutor.get<string>(ApiRoutes.ProxyServersBreadcrumb.replace(':proxyServerId', id));
    return data;
  });

export const useFetchSupportedOSProxyServerQuery = () => {
  return buildQuery<SupportedLinuxOS[], QueryError>([ProxyServersQueryKeys.GetSupportedOS], async () => {
    const { data } = await apiExecutor.get<SupportedLinuxOS[]>(ApiRoutes.ProxyServersSupportedOS);
    return data;
  });
};

export const useCheckProxyServerAddressesMutation = (id: string) => {
  return buildMutation<void, QueryError, void>([ProxyServersQueryKeys.CheckAddresses, id], async () => {
    await apiExecutor.post<void>(ApiRoutes.ProxyServerAddressesCheck.replace(':proxyServerId', id));
  });
};

export const useAddProxyServerAddressesMutation = (id: string) => {
  return buildMutation<ProxyServer, QueryError, { ips: string[] }>(
    [ProxyServersQueryKeys.AddAddresses, id],
    async (postData) => {
      const { data } = await apiExecutor.post<ProxyServer>(ApiRoutes.ProxyServerAddresses.replace(':proxyServerId', id), postData);
      return data;
    },
  );
};

